import { useCallback, useEffect, useRef, useState } from 'react';
import { createFileRoute, Outlet, useNavigate, useParams } from '@tanstack/react-router';
import { ChevronLeft, Grid3x3, Network } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { PageHeader } from '@/components/common/PageHeader';
import { LoadingRows, ErrorState } from '@/components/common/Feedback';
import { usePrefixes, useDeletePrefix } from '@/hooks/use-prefixes';
import { PrefixTree } from '@/components/prefix/PrefixTree';
import { CreatePrefixDialog } from '@/components/prefix/CreatePrefixDialog';
import { RangeExplorerDialog } from '@/components/prefix/RangeExplorerDialog';
import {
  buildTree,
  filterTree,
  flattenVisible,
  allExpandedIds,
  ancestorIds,
} from '@/components/prefix/tree-utils';
import type { FlatNode } from '@/components/prefix/tree-utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogClose,
} from '@/components/ui/dialog';
import { Mono } from '@/components/common/display';

export interface PrefixesSearch {
  q?: string;
}

export const Route = createFileRoute('/_auth/prefixes')({
  validateSearch: (search: Record<string, unknown>): PrefixesSearch => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
  component: PrefixesRoute,
});

function PrefixesRoute() {
  const { q } = Route.useSearch();
  const navigate = useNavigate();

  // Read active detail id from child route params (may be absent)
  const childParams = useParams({ strict: false }) as { id?: string };
  const activeId = childParams.id;

  const { data: prefixes = [], isLoading, error } = usePrefixes();
  const deletePrefix = useDeletePrefix();

  // Expanded set (manual toggles; q overrides with allExpanded)
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  // Highlighted row index for keyboard nav
  const [highlightedIndex, setHighlightedIndex] = useState(0);

  // Create dialog state
  const [createOpen, setCreateOpen] = useState(false);
  const [createParentId, setCreateParentId] = useState<string | undefined>(undefined);

  // Range-explorer dialog state
  const [explorerOpen, setExplorerOpen] = useState(false);

  // Delete confirm dialog state
  const [deleteTarget, setDeleteTarget] = useState<FlatNode | null>(null);

  // Track which q value we've already auto-navigated for
  const autoNavDoneRef = useRef<string | null>(null);

  // Build and optionally filter the tree
  const rawTree = buildTree(prefixes);
  const filteredTree = q && q.trim() ? filterTree(rawTree, q.trim()) : rawTree;
  const effectiveExpanded = q && q.trim() ? allExpandedIds(filteredTree) : expandedIds;
  const flatNodes: FlatNode[] = flattenVisible(filteredTree, effectiveExpanded);

  // Auto-navigate when q exactly matches a CIDR
  useEffect(() => {
    if (!q || q.trim() === '') return;
    const trimmed = q.trim();
    if (autoNavDoneRef.current === trimmed) return;
    const matched = prefixes.find((p) => p.cidr === trimmed);
    if (matched) {
      autoNavDoneRef.current = trimmed;
      void navigate({ to: '/prefixes/$id', params: { id: matched.id }, search: (p) => p });
    }
  }, [q, prefixes, navigate]);

  // Expand ancestors of active prefix when it changes
  useEffect(() => {
    if (!activeId || prefixes.length === 0) return;
    const ancestors = ancestorIds(prefixes, activeId);
    if (ancestors.size === 0) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      for (const aid of ancestors) next.add(aid);
      return next;
    });
  }, [activeId, prefixes]);

  // Keep highlighted index in sync with active id
  useEffect(() => {
    if (!activeId) return;
    const idx = flatNodes.findIndex((n) => n.item.id === activeId);
    if (idx >= 0) setHighlightedIndex(idx);
  // Intentionally omit flatNodes to avoid infinite loop — only re-sync when activeId changes
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  function toggleExpand(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSelect(id: string) {
    void navigate({ to: '/prefixes/$id', params: { id }, search: (p) => p });
  }

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (flatNodes.length === 0) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.min(i + 1, flatNodes.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        const node = flatNodes[highlightedIndex];
        if (node?.hasChildren && !node.isExpanded) toggleExpand(node.item.id);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        const node = flatNodes[highlightedIndex];
        if (node?.hasChildren && node.isExpanded) toggleExpand(node.item.id);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const node = flatNodes[highlightedIndex];
        if (node) handleSelect(node.item.id);
      } else if (e.key === 'n') {
        e.preventDefault();
        const node = flatNodes[highlightedIndex];
        setCreateParentId(node?.item.id);
        setCreateOpen(true);
      } else if (e.key === 'd') {
        e.preventDefault();
        const node = flatNodes[highlightedIndex];
        if (node) setDeleteTarget(node);
      }
    },
    // toggleExpand and handleSelect are stable inline functions; flatNodes/highlightedIndex are the real deps
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [flatNodes, highlightedIndex],
  );

  function confirmDelete() {
    if (!deleteTarget) return;
    const targetId = deleteTarget.item.id;
    const targetCidr = deleteTarget.item.cidr;
    deletePrefix.mutate(targetId, {
      onSuccess: () => {
        toast.success(`Prefijo ${targetCidr} eliminado`);
        setDeleteTarget(null);
        if (activeId === targetId) {
          void navigate({ to: '/prefixes', search: (p) => p });
        }
      },
      onError: (err) => {
        toast.error(err instanceof Error ? err.message : 'Error al eliminar el prefijo');
      },
    });
  }

  return (
    <div className="flex h-full min-h-0">
      {/* Left: tree pane — full width on mobile, hidden once a detail is open */}
      <div
        className={cn(
          'min-w-0 shrink-0 flex-col border-border md:w-[40%] md:border-r',
          activeId ? 'hidden w-full md:flex' : 'flex w-full',
        )}
      >
        <PageHeader
          title="Prefijos"
          icon={Network}
          actions={
            <>
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => setExplorerOpen(true)}
              >
                <Grid3x3 className="mr-1 size-3.5" />
                Desplegar rango
              </Button>
              <Button
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setCreateParentId(undefined);
                  setCreateOpen(true);
                }}
              >
                Nuevo prefijo
              </Button>
            </>
          }
        />

        <div className="min-h-0 flex-1 overflow-hidden">
          {isLoading ? (
            <div className="p-3">
              <LoadingRows rows={8} />
            </div>
          ) : error ? (
            <div className="p-4">
              <ErrorState error={error} />
            </div>
          ) : (
            <PrefixTree
              nodes={flatNodes}
              selectedId={activeId}
              highlightedIndex={highlightedIndex}
              onSelect={handleSelect}
              onHighlight={setHighlightedIndex}
              onToggleExpand={toggleExpand}
              onKeyDown={handleKeyDown}
            />
          )}
        </div>
      </div>

      {/* Right: detail outlet — full screen on mobile when open, placeholder on desktop */}
      <div
        className={cn(
          'min-h-0 min-w-0 flex-1 overflow-auto',
          activeId ? 'block' : 'hidden md:block',
        )}
      >
        {activeId ? (
          <>
            {/* Mobile-only back affordance to return to the tree */}
            <div className="sticky top-0 z-10 flex items-center border-b border-border bg-blaze-base/95 px-1 py-1.5 backdrop-blur md:hidden">
              <button
                type="button"
                onClick={() => void navigate({ to: '/prefixes', search: (p) => p })}
                className="flex items-center gap-1 rounded px-2 py-1.5 text-sm font-medium text-primary active:bg-accent"
              >
                <ChevronLeft className="size-4" />
                Prefijos
              </button>
            </div>
            <Outlet />
          </>
        ) : (
          <div className="hidden h-full items-center justify-center text-xs text-muted-foreground md:flex">
            Selecciona un prefijo
          </div>
        )}
      </div>

      {/* Create dialog */}
      <CreatePrefixDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        defaultParentId={createParentId}
      />

      <RangeExplorerDialog open={explorerOpen} onOpenChange={setExplorerOpen} />

      {/* Delete confirm dialog */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(o) => {
          if (!o) setDeleteTarget(null);
        }}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Eliminar prefijo</DialogTitle>
            <DialogDescription>Esta acción no se puede deshacer.</DialogDescription>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            ¿Eliminar <Mono>{deleteTarget?.item.cidr}</Mono>? Esta acción no se puede deshacer.
          </p>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline" size="sm">
                Cancelar
              </Button>
            </DialogClose>
            <Button
              variant="destructive"
              size="sm"
              onClick={confirmDelete}
              disabled={deletePrefix.isPending}
            >
              {deletePrefix.isPending ? 'Eliminando…' : 'Eliminar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
